/**
 * Avatar utility functions for consistent user avatar generation across the app
 * Used by CommentBubble1, ActiveCollaborators, and other components that display user avatars
 */

/**
 * Generate initials from user name
 * Follows the same pattern as ActiveCollaborators.tsx
 */
export const getInitials = (userName: string): string => {
  return (
    userName
      .split(' ')
      .map((name) => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??'
  );
};

/**
 * Generate consistent background color based on userName
 * Uses the same color palette as ActiveCollaborators.tsx
 *
 * Note: ActiveCollaborators uses userName + uid for uniqueness, but comments only have userName.
 * This provides consistency for the same userName, even if not perfectly unique across different users
 * with the same name.
 */
export const getAvatarColor = (userName: string, uid?: string): string => {
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-amber-500',
    'bg-yellow-500',
    'bg-lime-500',
    'bg-green-500',
    'bg-emerald-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-sky-500',
    'bg-blue-500',
    'bg-indigo-500',
    'bg-violet-500',
    'bg-purple-500',
    'bg-fuchsia-500',
    'bg-pink-500',
    'bg-rose-500',
  ];

  // Use uid if available (like ActiveCollaborators), otherwise just userName
  const hashInput = uid ? uid + userName : userName;
  let hash = 0;
  for (let i = 0; i < hashInput.length; i++) {
    hash = hashInput.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Generate initials from firstName and lastName (for use with UserProfile1)
 * This follows the pattern used in AuthenticatedSidebarLayout
 */
export const getInitialsFromNames = (firstName?: string, lastName?: string): string => {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
  return initials.toUpperCase() || '??';
};
